import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import type { TranslationKey } from '@kirigami/shared/i18n';
import { TranslatePipe, TranslationService } from '@kirigami/shared/i18n';
import { sanitizeHtmlWithLineBreaks } from '../utils/sanitize-html.helper';

/**
 * Landing-page dropzone. Accepts `.pdf`, `.docx` and `.tex` only, max 100 MB.
 * Emits the selected `File` on valid drop / click-choose; shows an
 * inline invalid-format badge for 3 s on reject.
 */
@Component({
  selector: 'app-uploadnow-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <div class="upload-now">
      <label for="landing-upload-file" class="upload-now__label">
        <div
          role="button"
          [attr.tabindex]="disabled ? -1 : 0"
          class="upload-now__button"
          [class.upload-now__button--disabled]="disabled"
          [attr.aria-disabled]="disabled"
          (click)="onButtonClick($event)"
          (keydown)="onKey($event)"
          (dragover)="onDragOver($event)"
          (drop)="onDrop($event)"
          aria-describedby="upload-help-type"
        >
          <div class="upload-now__body">
            <img src="img/upload-icon.svg" alt="" aria-hidden="true" class="upload-now__icon" />
            <span class="upload-now__cta">
              <span [innerHTML]="dragDropEmphasis()"></span>
              <span class="upload-now__cta-button" aria-hidden="true">
                {{ 'landing.uploadnow.uploadButton' | t }}
                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M8 11V2.5M8 2.5 4.5 6M8 2.5 11.5 6"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M2.5 10.5v2A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-2"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
            </span>
            <span class="upload-now__info">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="6.4" stroke="currentColor" stroke-width="1.3" />
                <path
                  d="m5.3 8.2 1.8 1.8 3.6-3.8"
                  stroke="currentColor"
                  stroke-width="1.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span [innerHTML]="infoFiles()"></span>
            </span>

            @if (invalidFileFormat()) {
              <div class="upload-now__invalid">
                <span class="upload-now__invalid-strong">{{
                  'landing.uploadnow.invalidFormatStrong' | t
                }}</span
                ><br />
                {{ 'landing.uploadnow.invalidFormatHint' | t }}
              </div>
            }
          </div>
        </div>
      </label>
      <input
        #fileInput
        id="landing-upload-file"
        type="file"
        [accept]="acceptFile"
        class="sr-only"
        (change)="onFileSelect($event)"
      />
      <p id="upload-help-type" class="sr-only" [innerHTML]="helpType()"></p>
    </div>
  `,
  styles: [
    `
      .upload-now {
        border-radius: 1rem;
        overflow: hidden;
        margin: 0 auto;
      }
      .upload-now__label {
        display: block;
      }
      .upload-now__button {
        display: flex;
        width: 100%;
        min-height: 20rem;
        padding: 2rem;
        border: 2px dashed var(--kirigami-300);
        border-radius: 1rem;
        background: var(--kirigami-50);
        text-align: center;
        cursor: pointer;
        transition:
          background 0.15s,
          border-color 0.15s;
        outline: none;
      }
      .upload-now__button:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 1px var(--neu-0),
          0 0 0 2px var(--neu-0),
          0 0 0 4px var(--kirigami-600);
      }
      .upload-now:hover .upload-now__button {
        background-color: var(--kirigami-100);
        border-color: var(--kirigami-500);
      }
      .upload-now__button--disabled,
      .upload-now:hover .upload-now__button--disabled {
        cursor: not-allowed;
        opacity: 0.55;
        background-color: var(--kirigami-50);
        border-color: var(--kirigami-300);
      }
      .upload-now__body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 1.375rem;
        margin: auto;
        padding: 0.5rem;
      }
      .upload-now__icon {
        width: 7.75rem;
        height: auto;
        display: block;
      }
      .upload-now__cta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: center;
        gap: 1.125rem;
        font-size: 1.25rem;
        color: var(--neu-900);
      }
      /* Visual pill only: the whole dropzone is the interactive control,
         so this stays a styled span rather than a nested button.
         kirigami-600 (not the design's 500) so the 14px label meets the
         4.5:1 AA contrast minimum. */
      .upload-now__cta-button {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        height: 3rem;
        padding: 0 1.625rem;
        border-radius: 999px;
        background: var(--kirigami-600);
        color: var(--neu-0);
        font-size: 0.875rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        transition: background-color 0.18s ease;
      }
      .upload-now__cta-button svg {
        width: 0.9375rem;
        height: 0.9375rem;
      }
      .upload-now:hover .upload-now__cta-button {
        background: var(--kirigami-700);
      }
      .upload-now:hover .upload-now__button--disabled .upload-now__cta-button {
        background: var(--kirigami-600);
      }
      .upload-now__info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--neu-600);
        line-height: 1.5;
      }
      .upload-now__info svg {
        width: 0.875rem;
        height: 0.875rem;
        flex: 0 0 auto;
      }
      .upload-now__invalid {
        color: var(--pink-600);
        font-size: 0.8em;
        text-align: center;
        margin-top: 0.5rem;
        border: solid 1px var(--pink-600);
        padding: 0.5rem 1.25rem;
        border-radius: 0.75rem;
      }
      .upload-now__invalid-strong {
        font-weight: 700;
      }
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    `,
  ],
})
export class UploadnowWidgetComponent {
  private readonly sanitizer = inject(DomSanitizer);

  @Input() isAuthenticated = false;

  /**
   * When true the dropzone is inert: clicks, keyboard activation, drops, and
   * file-input changes are all suppressed and no event is emitted. Used to
   * block new uploads while an analysis is already in progress.
   */
  @Input() disabled = false;

  @Output() readonly fileSelected = new EventEmitter<File>();
  @Output() readonly loginRequired = new EventEmitter<void>();
  @ViewChild('fileInput', { static: true }) fileInput!: ElementRef<HTMLInputElement>;

  /** Only `.docx`, `.pdf` and `.tex` extensions are accepted. */
  private readonly extensionsMap: Record<string, string> = {
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pdf: 'application/pdf',
    tex: 'application/x-tex',
  };

  /** Wide `accept` — actual validation runs against `extensionsMap`. */
  readonly acceptFile = 'application/*,text/markdown,image/*,text/plain,.tex';
  /** Hard size cap (≈100 MB). UI copy advertises 10 MB — see spec §11. */
  readonly maxFileSize = 100_000_000;

  readonly invalidFileFormat = signal(false);

  private readonly translations = inject(TranslationService);

  /**
   * Copy that carries authored line breaks and inline emphasis, so it is bound
   * through `[innerHTML]` and has to be sanitized. Computed rather than fixed
   * so a locale change re-sanitizes the new text.
   */
  readonly dragDropEmphasis = computed<SafeHtml>(() =>
    this.safe('landing.uploadnow.dragDropEmphasis'),
  );
  readonly infoFiles = computed<SafeHtml>(() => this.safe('landing.uploadnow.infoFiles'));
  readonly helpType = computed<SafeHtml>(() => this.safe('landing.uploadnow.helpType'));

  private safe(key: TranslationKey): SafeHtml {
    return sanitizeHtmlWithLineBreaks(this.sanitizer, this.translations.t(key));
  }

  onFileSelect(event: Event): void {
    if (this.disabled) return;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file && this.validate(file)) this.fileSelected.emit(file);
  }

  /**
   * Click on the dropzone. The wrapping `<label for>` would open the native OS
   * file picker; for anonymous visitors we suppress that (preventDefault) and
   * emit `loginRequired` so the landing shows the login-required modal.
   * Authenticated visitors fall through — native label behaviour opens the picker.
   */
  onButtonClick(event: MouseEvent): void {
    // Blocked while an analysis is running: swallow the click (the wrapping
    // `<label>` would otherwise open the native picker) and emit nothing.
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    if (!this.isAuthenticated) {
      event.preventDefault();
      this.loginRequired.emit();
    }
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.disabled) return;
      if (!this.isAuthenticated) {
        this.loginRequired.emit();
        return;
      }
      this.fileInput.nativeElement.click();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.disabled) return;
    if (!this.isAuthenticated) {
      this.loginRequired.emit();
      return;
    }
    const file = event.dataTransfer?.files?.[0];
    if (file && this.validate(file)) this.fileSelected.emit(file);
  }

  private validate(file: File): boolean {
    if (!this.matchesAccept(file)) {
      this.invalidFileFormat.set(true);
      setTimeout(() => this.invalidFileFormat.set(false), 3000);
      this.clear();
      return false;
    }
    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.clear();
      return false;
    }
    return true;
  }

  private matchesAccept(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension) return false;
    return Boolean(this.extensionsMap[extension]);
  }

  private clear(): void {
    this.fileInput.nativeElement.value = '';
  }
}
